/**
 * useHomeDeepLink - Hook for home/chat deep linking
 *
 * Provides URL-driven state management for chat navigation:
 * - Reads chatId, messageId from URL
 * - Provides navigation functions that update URL
 * - Syncs URL state with component state
 *
 * URL Structure:
 * - /home                      → New chat screen (no active chat)
 * - /home/:chatId              → Shows specific chat
 * - /home/:chatId/:messageId   → Shows chat with message modal open
 *
 * Usage:
 * ```tsx
 * function HomeView() {
 *   const {
 *     chatId,
 *     messageId,
 *     isMessageModalOpen,
 *     navigateToChat,
 *     openMessageModal,
 *     closeMessageModal,
 *     navigateToNewChat,
 *   } = useHomeDeepLink();
 *
 *   return (
 *     <>
 *       <ChatContent chatId={chatId} />
 *       {isMessageModalOpen && (
 *         <MessageDetailPanel
 *           messageId={messageId}
 *           onClose={closeMessageModal}
 *         />
 *       )}
 *     </>
 *   );
 * }
 * ```
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// =============================================================================
// TYPES
// =============================================================================

export interface UseHomeDeepLinkResult {
  /** Current chat ID from URL (undefined = new chat screen) */
  chatId: string | undefined;

  /** Message ID if modal is open (from URL) */
  messageId: string | undefined;

  /** Whether a message modal is currently open */
  isMessageModalOpen: boolean;

  /** Whether we're on the new chat screen (no chatId) */
  isNewChatScreen: boolean;

  /** Navigate to a specific chat */
  navigateToChat: (chatId: string) => void;

  /** Navigate to new chat screen */
  navigateToNewChat: () => void;

  /** Open message detail modal (updates URL) */
  openMessageModal: (messageId: string) => void;

  /** Close message modal (removes messageId from URL) */
  closeMessageModal: () => void;

  /** Copy current URL to clipboard */
  copyLink: () => Promise<void>;

  /** Get current full URL */
  getCurrentUrl: () => string;
}

// =============================================================================
// HOOK
// =============================================================================

export function useHomeDeepLink(): UseHomeDeepLinkResult {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL manually
  // URL patterns:
  // - /home                      → New chat screen
  // - /home/:chatId              → Shows specific chat
  // - /home/:chatId/:messageId   → Shows chat with message modal
  const urlParts = useMemo(() => {
    const match = location.pathname.match(/\/home(?:\/([^/]+))?(?:\/([^/]+))?/);

    if (!match) {
      return { chatId: undefined, messageId: undefined };
    }

    return {
      chatId: match[1] || undefined,
      messageId: match[2] || undefined,
    };
  }, [location.pathname]);

  const { chatId, messageId } = urlParts;

  const isMessageModalOpen = !!messageId;
  const isNewChatScreen = !chatId;

  // === NAVIGATION FUNCTIONS ===

  const navigateToChat = useCallback(
    (newChatId: string) => {
      navigate(`/home/${newChatId}`);
    },
    [navigate]
  );

  const navigateToNewChat = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  const openMessageModal = useCallback(
    (newMessageId: string) => {
      if (!chatId) {
        console.warn('[useHomeDeepLink] Cannot open message modal without active chat');
        return;
      }
      navigate(`/home/${chatId}/${newMessageId}`);
    },
    [chatId, navigate]
  );

  const closeMessageModal = useCallback(() => {
    if (!chatId) return;
    navigate(`/home/${chatId}`);
  }, [chatId, navigate]);

  const copyLink = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, []);

  const getCurrentUrl = useCallback(() => {
    return window.location.href;
  }, []);

  return {
    chatId,
    messageId,
    isMessageModalOpen,
    isNewChatScreen,
    navigateToChat,
    navigateToNewChat,
    openMessageModal,
    closeMessageModal,
    copyLink,
    getCurrentUrl,
  };
}

/**
 * Helper to build home/chat URLs without navigation
 */
export function buildHomeUrl(chatId?: string, messageId?: string): string {
  if (!chatId) return '/home';
  if (!messageId) return `/home/${chatId}`;
  return `/home/${chatId}/${messageId}`;
}
